import jsPDF from 'jspdf';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { projectService } from '../services/projectService';
import {
  DEBUG_SCALE_RULERS,
  FEET_PER_METER,
  MM_PER_INCH,
  logicalCanvasSize,
  selectPageOrientation,
  sizeImageToExactScaleMm,
  drawScaleRulerMm,
  drawDebugEdgeRulers,
} from './pdfScaleUtils';
import { trimImageWhitespace } from './pdfImageUtils';

const LETTER_MM = { short: 215.9, long: 279.4 };

export const exportFloorplanToPDF = async (
  canvasRef,
  projectName = 'Structure',
  viewType = 'Floorplan',
  projectId = null,
  saveToProject = true,
  pixelsPerMeter = null
) => {
  try {
    const canvas = canvasRef.current;
    if (!canvas) {
      throw new Error('Canvas reference not found');
    }

    // `pixelsPerMeter` is in LOGICAL px (the canvas's scale state), so the
    // image size must be logical too — canvas.width is the DPR-scaled
    // backing store and would inflate the feet the image covers by DPR.
    const logical = logicalCanvasSize(canvas);
    const pixelsPerFoot =
      pixelsPerMeter && pixelsPerMeter > 0
        ? pixelsPerMeter / FEET_PER_METER
        : null;

    // Crop the capture to its ink (px/ft unchanged, logical size × crop
    // fraction) so the page fit is about the drawing, not the viewport.
    const trimmed = await trimImageWhitespace(canvas.toDataURL('image/png'));
    const imgData = trimmed?.dataUrl ?? canvas.toDataURL('image/png');
    const imgFormat = trimmed ? 'JPEG' : 'PNG';
    const imageW = logical.width * (trimmed?.fraction.x ?? 1);
    const imageH = logical.height * (trimmed?.fraction.y ?? 1);

    // Letter page; orientation picked per drawing so a tall plan isn't
    // forced down a scale step by a landscape sheet's height.
    const marginX = 10;
    const marginY = 30; // Leave space for title and timestamp
    const marginBottom = 10;
    const usable = (pageW, pageH) => ({
      w: (pageW - 2 * marginX) / MM_PER_INCH,
      h: (pageH - marginY - marginBottom) / MM_PER_INCH,
    });
    const orientation = pixelsPerFoot
      ? selectPageOrientation(pixelsPerFoot, imageW, imageH, {
          landscape: usable(LETTER_MM.long, LETTER_MM.short),
          portrait: usable(LETTER_MM.short, LETTER_MM.long),
        })
      : 'landscape';
    const pdf = new jsPDF({ orientation, unit: 'mm', format: 'letter' });

    // Get page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    drawFloorPlanHeader(pdf, `${projectName} - ${viewType}`);

    // Calculate image dimensions to fit page with margins
    const maxWidth = pageWidth - 2 * marginX;
    const maxHeight = pageHeight - marginY - marginBottom;

    let imgWidth, imgHeight;
    let placedScale = null;

    if (pixelsPerFoot) {
      const result = sizeImageToExactScaleMm(
        pixelsPerFoot,
        imageW,
        imageH,
        maxWidth,
        maxHeight
      );
      imgWidth = result.width;
      imgHeight = result.height;
      placedScale = result.scale;
    } else {
      // Fallback: fit to available space
      const aspectRatio = imageW / imageH;
      if (aspectRatio > maxWidth / maxHeight) {
        imgWidth = maxWidth;
        imgHeight = imgWidth / aspectRatio;
      } else {
        imgHeight = maxHeight;
        imgWidth = imgHeight * aspectRatio;
      }
    }

    // Center the image horizontally
    const imgX = marginX + (maxWidth - imgWidth) / 2;

    // Add image to PDF
    pdf.addImage(imgData, imgFormat, imgX, marginY, imgWidth, imgHeight);

    // Add scale ruler or footer
    if (placedScale) {
      drawScaleRulerMm(
        pdf,
        placedScale,
        marginX + maxWidth,
        marginY + imgHeight + 2
      );
    } else {
      const footerY = marginY + imgHeight + 5;
      if (footerY < pageHeight - 10) {
        pdf.setFontSize(8);
        pdf.setTextColor(100);
        pdf.text(
          'Note: Measurements shown are in feet and inches',
          marginX,
          footerY
        );
      }
    }

    // Debug: draw 1" interval rulers along page edges
    if (DEBUG_SCALE_RULERS) {
      drawDebugEdgeRulers(pdf, pageWidth, pageHeight, MM_PER_INCH);
    }

    const { filename, filed } = await saveFloorPlanPdfFiled(pdf, {
      projectName,
      viewType,
      projectId: saveToProject ? projectId : null,
    });

    return { success: true, filename, filed };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
};

const TITLE_FONT_SIZE = 16;
const MIN_TITLE_FONT_SIZE = 11;

/**
 * Title and generation time in the header band of a letter floor-plan sheet.
 * With `maxWidth` (mm) a long title steps its font down, then ends in an
 * ellipsis, rather than running under whatever the header carries on its
 * right.
 */
export const drawFloorPlanHeader = (pdf, title, { maxWidth } = {}) => {
  pdf.setFont(undefined, 'bold');
  let size = TITLE_FONT_SIZE;
  pdf.setFontSize(size);
  while (
    maxWidth &&
    size > MIN_TITLE_FONT_SIZE &&
    pdf.getTextWidth(title) > maxWidth
  ) {
    pdf.setFontSize(--size);
  }
  const fitted =
    maxWidth && pdf.getTextWidth(title) > maxWidth
      ? `${pdf.splitTextToSize(title, maxWidth - pdf.getTextWidth('...'))[0]}...`
      : title;
  pdf.text(fitted, 10, 15);

  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.text(`Generated: ${new Date().toLocaleString()}`, 10, 22);
};

/**
 * Download a floor-plan PDF, first filing it under the project's floor-plan
 * documents when `projectId` is given. The cloud save is best-effort: if it
 * fails the download still happens.
 *
 * @returns {Promise<{filename: string, filed: boolean}>} `filed` once the
 *   upload and the project's list were both written
 */
export const saveFloorPlanPdfFiled = async (
  pdf,
  { projectName, viewType, projectId = null, filenamePrefix = 'floorplan' }
) => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const filename = `${filenamePrefix}_${projectName.replace(/\s+/g, '_')}_${timestamp}.pdf`;
  let filed = false;

  if (projectId) {
    try {
      const pdfBlob = pdf.output('blob');
      const storagePath = `projects/${projectId}/floor-plans/${filename}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, pdfBlob);
      const downloadUrl = await getDownloadURL(storageRef);

      const project = await projectService.getProject(projectId);
      const floorPlanDocs = project.floorPlanDocs || [];
      floorPlanDocs.push({
        filename,
        url: downloadUrl,
        createdAt: new Date().toISOString(),
        type: 'floor-plan',
        storagePath,
        viewType,
      });
      await projectService.updateProject(projectId, { floorPlanDocs });
      filed = true;

      console.log('Floor plan saved to project documents');
    } catch (error) {
      console.error('Error saving floor plan to project:', error);
    }
  }

  pdf.save(filename);
  return { filename, filed };
};

/** `saveFloorPlanPdfFiled`, answering the filename only. */
export const saveFloorPlanPdf = async (pdf, options) =>
  (await saveFloorPlanPdfFiled(pdf, options)).filename;
